import { exists } from '@jack-henry/frontend-utils/functions';
import { ObservationSource } from '@jack-henry/frontend-utils/observable';
import type { Action } from '@jack-henry/frontend-utils/types';
import { debounce, hasContent, type DeepRequired } from '@treasury/utils';
import { css, html, nothing, PropertyValues, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ifDefined } from 'lit/directives/if-defined.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { html as staticHtml, unsafeStatic } from 'lit/static-html.js';
import { TmBaseComponent } from '../../../index';
import {
    JhaAdvancedTable,
    JhaAdvancedTableBulkSelectEvent,
    JhaAdvancedTableClickEvent,
    JhaAdvancedTableConfig,
    JhaAdvancedTableRowActionEvent,
    JhaAdvancedTableUpdateEvent,
    JhaAdvancedTableViewChangeEvent,
} from './jha-advanced-table.types';
import { TableViewManager, TableViewState } from './table-view-manager';
import {
    EnrichedTableItem,
    EnrichedTableItemKey,
    TmTableActionInternal,
    TmTableSelectActionInternal,
    TmTableSelectCustomDefInternal,
    type TmTableOptionsNormalized,
} from './tm-table-internal.types';
import { dispatchTableEvent, unwrapEnriched } from './tm-table.helpers';
import { mapCols, mapInitialSort, mapRowActions, mapViews } from './tm-table.mappings';
import { normalizeOptions } from './tm-table.normalization';
import { AllViewId, SingleViewId, TmTableEvent, type TmTableOptions } from './tm-table.types';

import '@banno/jha-wc/src/tables/advanced/jha-advanced-table/jha-advanced-table';
import '@jack-henry/jh-ui/components/button/button.js';

export const tagName = 'tm-table';

const DEBOUNCE_DELAY = 1000;

/**.;
 * Advanced table for use in modern TM applications.
 */
@customElement(tagName)
export class TmTable<T extends object = object> extends TmBaseComponent {
    public static readonly styles = css`
        :host {
            --jha-advanced-table-controls-padding-x: var(--tm-table-chrome-padding-x, 4px);
            --jha-advanced-table-controls-padding-y: var(--tm-table-chrome-padding-y, 16px);
            --jha-advanced-table-header-margin-x: var(--tm-table-chrome-padding-x, 0);
            --jha-table-row-action-cursor: pointer;
        }
        ::slotted([slot='sub-header']),
        .sub-header {
            padding-top: 1em;
            padding-bottom: 1em;
        }
        .title {
            font-size: var(--tm-table-title-font-size, 2rem);
        }
    `;

    /**
     * The initial incoming dataset used to seed the table across all views.
     */
    @property({ attribute: false, reflect: false })
    public items?: T[];

    @property({ type: Object })
    public readonly options?: TmTableOptions<T> = {};

    @property({ type: String })
    /**
     * The main title displayed for the entire table on all views.
     */
    public readonly header?: string;

    @property({ type: Boolean })
    public loading = false;

    @property({
        reflect: false,
        attribute: false,
    })
    public initialActive?: T;

    /**
     * The subset of items being shown in the current view.
     *
     * This value is *all* items available to the view and is not paginated or otherwise
     * subdivided beyond that. Pagination logic is handled by `<jha-advanced-table>`.
     */
    @state()
    private viewItems?: EnrichedTableItem<T>[];

    @state()
    private viewId?: string;

    /**
     * The normalized configuration combining consumer-specified
     * options with sensible defaults for what is omitted.
     */
    @state()
    private fullOptions?: TmTableOptionsNormalized<T>;

    /**
     * If `true` and a custom multi-select UI is provided,
     * displays the content beneath the bulk action button.
     */
    @state()
    private customMultiSelectVisible = false;

    private readonly advancedTableRef = createRef<JhaAdvancedTable>();

    private get advancedTable() {
        if (!this.advancedTableRef.value) {
            throw new Error('Could not get advanced table value');
        }

        return this.advancedTableRef.value;
    }

    private readonly viewManager = new TableViewManager<T>();

    private cleanupTasks: Action[] = [];

    private readonly searchCompletedSource = new ObservationSource<void>();

    /**
     * This primarily exists to accommodate test scenarios since searching is debounced.
     *
     * Not ideal since using this goes against the Vitest browser mode testing paradigm,
     * but there isn't a great way to know in the UI.
     */
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public readonly searchCompleted$ = this.searchCompletedSource.toObservable();

    /**
     * Perform a debounced search given a valid search term.
     *
     * This has to be a property rather than a method so that debounce() can be applied to it.
     */
    private performSearch = debounce((searchTerm: string) => {
        const doPostQuery = () => {
            this.advancedTable.loading = false;
            this.searchCompletedSource.emit();
        };
        this.advancedTable.loading = true;
        const searchResult = this.viewManager.search(searchTerm);

        if (searchResult instanceof Promise) {
            // use finally() instead of await to allow for synchronous execution of non-promise results
            searchResult.finally(doPostQuery);
        } else {
            // account for non-promise results to avoid displaying loader
            // and instant feedback for in-memory filtering of data
            doPostQuery();
        }
    }, DEBOUNCE_DELAY);

    public get _loading() {
        return this.loading || !exists(this.items);
    }

    public render() {
        if (!this.viewManager.initialized) {
            return nothing;
        }

        const {
            selectActions,
            multiSelectUiDef,
            columns,
            showAllView,
            isSingleView,
            noContentMessage,
            searchPlaceholder,
            allowMultiSelect,
            filters,
            subtitle,
            tableActions,
            paginationEnabled,
            pageLimits,
            allowRefresh,
            initialSort,
        } = this.viewManager;

        /**
         * The configuration shape used internally by `<jha-advanced-table>`.
         */
        const advancedTableConfig: Partial<JhaAdvancedTableConfig> = {
            apiControlled: false,
            activeProperty: EnrichedTableItemKey.IsActive,
            sort: isSingleView ? mapInitialSort(initialSort, columns.definitions) : undefined,
        };
        const views = mapViews(this.viewManager.views);
        const allowPageLimitChange = pageLimits.length > 0;
        const rowActions = mapRowActions(this.viewManager.rowActions);
        const [pageLimit] = pageLimits;

        return html`<jha-advanced-table
            striped
            ${ref(this.advancedTableRef)}
            .data=${this.viewItems}
            .tableConfig=${advancedTableConfig}
            .columns=${mapCols(columns, filters)}
            .canEditColumns=${columns.canEdit}
            .loading=${this._loading}
            .views=${views}
            .activeViewId=${this.viewId}
            .bulkActions=${allowMultiSelect}
            .noPagination=${!paginationEnabled}
            .selectedItemIdentifier=${EnrichedTableItemKey.TableId}
            .showViews=${!isSingleView}
            .hideAllView=${!showAllView}
            .pageLimitOptions=${pageLimits}
            .allowPageLimitChange=${allowPageLimitChange}
            .allowRefresh=${allowRefresh}
            .noContentMessage=${typeof noContentMessage === 'string' ? noContentMessage : ''}
            .searchPlaceholderText=${searchPlaceholder}
            .rowActions=${rowActions}
            .pageLimit=${pageLimit}
            @table-cell-click=${(e: JhaAdvancedTableClickEvent<T>) => this.handleClick(e, 'single')}
            @table-cell-double-click=${(e: JhaAdvancedTableClickEvent<T>) =>
                this.handleClick(e, 'double')}
            @view-change=${(e: JhaAdvancedTableViewChangeEvent) => this.handleViewChange(e)}
            @table-update=${(e: JhaAdvancedTableUpdateEvent) => this.handleTableUpdate(e)}
            @bulk-select=${(e: JhaAdvancedTableBulkSelectEvent) => this.handleBulkSelect(e)}
            @pagination-forward=${() => this.handlePagination('forward')}
            @pagination-backward=${() => this.handlePagination('backward')}
            @refresh-table-data=${() => this.handleRefresh()}
            @table-row-action=${(e: JhaAdvancedTableRowActionEvent) => this.handleRowAction(e)}
        >
            ${this.renderHeader(this.header, subtitle)} ${this.renderSubheader()}
            ${renderActionBar('table-controls', tableActions)}
            ${renderNoContentMessage(noContentMessage)}
            ${this.renderBulkActions(multiSelectUiDef ? multiSelectUiDef : selectActions)}
        </jha-advanced-table>`;
    }

    public connectedCallback() {
        super.connectedCallback();
        const viewStateHandle = this.viewManager.viewState$.subscribe(state =>
            this.handleStatedUpdated(state)
        );
        const activeItemHandle = this.viewManager.activeItem$.subscribe(item => {
            const unwrapped = item ? unwrapEnriched<T>(item) : item;
            dispatchTableEvent(this, TmTableEvent.ActiveItemChange, unwrapped);
        });

        this.cleanupTasks.push(() => viewStateHandle.unsubscribe());
        this.cleanupTasks.push(() => activeItemHandle.unsubscribe());
        this.cleanupTasks.push(() => this.viewManager.finalize());
    }

    public disconnectedCallback() {
        super.disconnectedCallback();
        this.cleanupTasks.forEach(cleanup => cleanup());
        this.cleanupTasks = [];
    }

    /**
     * Change the active view to the one at the specified index.
     *
     * @param index The zero-based index of the view to activate,
     * corresponding to the order in which views were provided in the
     * table options.
     */
    public changeView(index: number) {
        this.viewManager.setActiveView(index);
    }

    protected updated(changedProps: PropertyValues) {
        if (changedProps.has('items') || changedProps.has('options')) {
            this.trySetFullOptions();

            if (this.fullOptions) {
                this.viewManager.init(this.fullOptions);
            }

            if (this.items) {
                const pageLimit = this.advancedTableRef.value?.pageLimit;
                this.viewManager.setData(this.items, pageLimit);
            }
        }
    }

    private trySetFullOptions() {
        if (!exists(this.options)) {
            return;
        }

        const { initialActive, items, options } = this;
        const firstItem = items && items.length > 0 ? items[0] : undefined;

        this.fullOptions = normalizeOptions(
            {
                ...options,
                initialActive,
            },
            firstItem
        );
    }

    private handleStatedUpdated(state: TableViewState<T>) {
        const { data, viewId } = state;

        if (viewId !== AllViewId && viewId !== SingleViewId) {
            this.viewId = viewId;
        }

        // keep items in sync with data filtered through a view
        if (data !== this.viewItems) {
            this.viewItems = data;
        }
    }

    private handleClick(e: JhaAdvancedTableClickEvent<T>, type: 'single' | 'double') {
        const { item, colId } = e.detail;
        const externalPayload = { type, item, columnKey: colId as keyof T };

        this.viewManager.click(externalPayload);
        this.advancedTable.requestUpdate();
        dispatchTableEvent(this, TmTableEvent.Click, externalPayload);
    }

    private handlePagination(direction: 'forward' | 'backward') {
        if (direction === 'forward') {
            this.viewManager.pageForward();
        } else {
            this.viewManager.pageBackward();
        }

        const { pageNumber } = this.viewManager;
        const items = this.advancedTable.visibleSlice.map(i =>
            unwrapEnriched(i as EnrichedTableItem<T>)
        );
        dispatchTableEvent(this, TmTableEvent.Paginate, {
            direction,
            pageNumber,
            items,
        });
    }

    private async handleTableUpdate(e: JhaAdvancedTableUpdateEvent) {
        const { type, config } = e.detail;

        switch (type) {
            case 'searchQuery': {
                const { searchQuery = '' } = config;
                const { length } = searchQuery;
                const { minimumQueryLength } = this.viewManager;

                if (length > 0 && length < minimumQueryLength) {
                    return;
                }

                // for empty results, display loader immediately to cover up all results
                // being shown by the advanced table's in-memory filtering before
                // reduceData() updates the internal items array
                if (length < 1 && this.viewItems && this.viewItems.length > 0) {
                    this.advancedTable.loading = true;
                }

                this.performSearch(searchQuery);
                break;
            }
            case 'view-reset': {
                this.viewManager.resetView();
                break;
            }
            default:
                break;
        }
    }

    private handleRefresh() {
        return this.viewManager.refreshData();
    }

    private handleBulkSelect(e: JhaAdvancedTableBulkSelectEvent) {
        const { items: itemIds } = e.detail;
        const countChanged = this.viewManager.selected.length !== itemIds.length;

        this.viewManager.setSelected(itemIds);
        if (itemIds.length === 0) {
            this.customMultiSelectVisible = false;
        } else if (countChanged && this.customMultiSelectVisible) {
            this.requestUpdate();
        }

        dispatchTableEvent(this, TmTableEvent.Select, { items: this.viewManager.selected });
    }

    private handleViewChange(e: JhaAdvancedTableViewChangeEvent) {
        // empty string represents the "all" view in the <jha-table-views> component
        const viewId = e.detail === '' ? AllViewId : e.detail;
        this.viewManager.setActiveView(viewId);
        this.clearSelected();

        // dispatch empty select event for consumer reset
        dispatchTableEvent(this, TmTableEvent.Select, { items: [] });
    }

    private renderBulkActions(
        actionsOrDef:
            | DeepRequired<TmTableSelectActionInternal<T>>[]
            | TmTableSelectCustomDefInternal<T> = []
    ) {
        const slotName = 'table-actions';
        if (Array.isArray(actionsOrDef)) {
            return renderActionBar(slotName, actionsOrDef, () => this.clearSelected());
        }

        const { label } = actionsOrDef;
        /**
         * The action button only serves to toggle the visibility of the custom multi-select UI.
         */
        const action = {
            label,
            execute: () => {
                this.customMultiSelectVisible = !this.customMultiSelectVisible;
            },
        };
        return renderActionBar(
            slotName,
            [action],
            undefined,
            this.renderMultiSelectUi(actionsOrDef)
        );
    }

    private renderHeader(title?: string, subtitle?: string) {
        if (!exists(title) || title.length < 1) {
            if (exists(subtitle) && subtitle.length > 1) {
                this.logger.logWarning(
                    `Tried to render header with subtitle "${subtitle}" but no main header text.`
                );
            }
            return nothing;
        }

        const subtitleElement =
            subtitle && subtitle.length > 0
                ? html`<div class="text-sm">${subtitle}</div>`
                : nothing;

        // should this target a slot in <jha-advanced-table>?
        return html`<div class="flex flex-col ">
            <div class="leading-[2.4rem] ml-1 title">${title}</div>
            ${subtitleElement}
        </div>`;
    }

    private renderSubheader() {
        const viewSubheader = this.viewManager.renderSubheader();

        // view-specific sub-headers take precedence over slot content
        if (viewSubheader !== nothing) {
            return html`<div class="sub-header">${viewSubheader}</div>`;
        }

        return html`<slot name="sub-header"></slot>`;
    }

    private renderMultiSelectUi(def: TmTableSelectCustomDefInternal<T>) {
        if (!this.customMultiSelectVisible) {
            return nothing;
        }

        const content = def.render(() => {
            this.clearSelected();
            this.customMultiSelectVisible = false;
        });

        // absolutely position the content relative to the parent container
        // in the slot so it appears below the button
        return html`<div
            role="dialog"
            style="z-index: 9999;"
            class="absolute top-[40px] right-0 bg-[--jha-background-color] mt-2.5 rounded-sm  shadow-md p-4"
        >
            ${content}
        </div>`;
    }

    private clearSelected() {
        this.advancedTable.selectedItems = [];
    }

    private handleRowAction(e: JhaAdvancedTableRowActionEvent) {
        const { item, action } = e.detail;

        this.viewManager.executeRowAction(item as T, action);
    }
}

/**
 * If the configuration returned a `TemplateResult`, render it into
 * the `no-content-message` slot instead of binding to the string attribute.
 */
function renderNoContentMessage(noContent: TemplateResult | string) {
    if (typeof noContent === 'string') {
        // render nothing since the value should have been
        // bound to the attribute on <jha-advanced-table>
        return nothing;
    }

    return html`<div slot="no-content-message">${noContent}</div>`;
}

/**
 * Render a bar of actions in the appropriate slot of the advanced table.
 *
 * Provides visual consistency when rendering table action buttons under different contexts.
 *
 * @param postExecuteCallback A callback to invoke after the externally-provided action occurs.
 */
function renderActionBar<T>(
    slotName: 'table-controls' | 'table-actions',
    actions?: TmTableActionInternal<T>[],
    postExecuteCallback?: Action,
    additionalContent: TemplateResult | typeof nothing = nothing
) {
    if (!exists(actions) || actions.length < 1) {
        return nothing;
    }

    const buttons = actions.map(({ icon, tooltip, execute: originalExecute, label }) => {
        const execute = () => {
            const p = originalExecute() || Promise.resolve();
            return p.then(postExecuteCallback);
        };
        return createJhButton(execute, icon, label, tooltip);
    });

    // use relative positioning so that content can be absolutely positioned within it
    return html`<div slot="${slotName}" class="py-3">
        <div
            class="relative flex flex-row items-center flex-nowrap justify-between gap-2 min-w-2 min-h-2"
        >
            ${buttons}${additionalContent}
        </div>
    </div>`;
}

/**
 * Create a `<jh-button>` element to be used in a multi-select or table action toolbar context.
 */
function createJhButton(
    execute: Action | (() => Promise<void>),
    icon?: string,
    label?: string,
    tooltip?: string
) {
    // normalize empty string labels to undefined so they are removed;
    // otherwise, they add extra whitespace to the button
    if (exists(label) && label.length < 1) {
        label = undefined;
    }

    // since we control the icon string, there's no risk of XSS here
    const iconTag = hasContent(icon) ? unsafeStatic(`jh-icon-${icon}`) : undefined;
    const iconElement = iconTag
        ? staticHtml`<${iconTag} slot="jh-button-icon"></${iconTag}>`
        : nothing;
    const titleText = tooltip ? tooltip : label;
    const appearance = exists(label) ? 'primary' : 'tertiary';

    if (!exists(iconTag) && !hasContent(label)) {
        throw new Error('Either an icon or a label must be provided for an action button.');
    }

    return staticHtml`<jh-button
        size="small"
        label="${ifDefined(label)}"
        appearance="${appearance}"
        title="${ifDefined(titleText)}"
        @click=${() => execute()}
        
    >
        ${iconElement}
    </jh-button>`;
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmTable;
    }
}
